import * as React from 'react';

import { setForbiddenHandler, setUnauthorizedHandler } from '@/domains/api';
import { authApi } from './api';
import { UPDATE_INTERVAL } from './constants';
import { endSessionUrl } from './idp';
import { ValidationStatus, checkSession } from './session-helpers';
import { sessionStore } from './storage';
import type { IAuthError, ISession, IUser } from './types';

/** `pending` while the first pass reads `/auth/me`, and while a logout leaves for the IdP: the
 *  guards draw the splash for both. */
type AuthStatus = 'pending' | 'authenticated' | 'anonymous';

interface IAuthContextValue {
  status: AuthStatus;
  isAuth: boolean;
  user: Nullable<IUser>;
  error: Nullable<IAuthError>;
  /**
   * Trades the IdP's access token for survey-api's session and adopts it, keeping `idToken` for
   * logout. Rejects with the exchange's `ApiError` — `/auth/callback` says which refusal it was —
   * and with nothing adopted.
   */
  exchange: (accessToken: string, idToken: string) => Promise<void>;
  /**
   * Posts `/auth/logout`, then opens the IdP's end session when the session holds an id_token —
   * which signs the person out of Entra too, and leaves them on the IdP's page, since a portal
   * client has no post-logout redirect. The splash stands from the press to that page: `/login`
   * between the two would be a sign-in page nobody stays on. A session without one (stored by
   * the password build) is dropped at once and ends at `/login`.
   */
  signout: () => void;
  /**
   * Re-reads `/auth/me` for the session this browser holds, and adopts the answer only while
   * that session is still the one it asked about (`sessionEpoch`).
   *
   * For the two facts about an account that change outside this tab: whether its address is
   * confirmed — the confirmation link is opened wherever the mail is read, usually a new tab, and
   * redeeming it touches no session, so nothing else would tell this tab — and what it may do,
   * since an administrator changes a role's permissions or an account's roles from another
   * browser (SR-228; `TeamShell` asks on a shown tab). Resolves quietly
   * when the read fails: a 401 has already gone through the handler that drops the session,
   * and anything else leaves the account as it was last read, which is what a prompt can live
   * with. A no-op with no session.
   */
  refreshUser: () => Promise<void>;
}

const AuthContext = React.createContext<Nullable<IAuthContextValue>>(null);

const SESSION_EXPIRED: IAuthError = {
  type: 'session',
  message: 'Your session has expired. Please sign in again.'
};

/** How long a logout that leaves for the IdP's end session waits for `POST /auth/logout`. */
const LOGOUT_WAIT_MS = 5_000;

/**
 * Session lifecycle against survey-api.
 *
 * Invent's IdP is met once per sign-in (`/auth/callback`, `idp.ts`) and once per logout; between
 * the two the session is survey-api's, an opaque token against a server-side session row:
 *
 * - **No refresh.** There is no endpoint to call and nothing in the token to
 *   renew, so an expired session is signed out rather than silently extended.
 * - **The user comes from the server.** `GET /auth/me` is the only source of
 *   the account id, address, kind and permissions — there are no claims to decode.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = React.useState<AuthStatus>('pending');
  const [user, setUser] = React.useState<Nullable<IUser>>(null);
  const [error, setError] = React.useState<Nullable<IAuthError>>(null);

  /**
   * Bumped whenever the session changes identity — adopted or dropped.
   *
   * The 403 re-read below is the only asynchronous thing here that can outlive the session it
   * was asking about, and an unmount guard would not catch it: this provider is mounted for
   * the life of the app, so its cleanup never runs. A `/auth/me` in flight across a sign-out
   * or a new sign-in is answering about a session that has ended, and adopting its answer
   * would put the departed account back into context.
   */
  const sessionEpoch = React.useRef(0);

  /** Set while a logout leaves for the IdP, until the session is dropped. */
  const leaving = React.useRef(false);

  const dropSession = React.useCallback((authError: Nullable<IAuthError> = null) => {
    sessionEpoch.current += 1;
    leaving.current = false;
    sessionStore.remove();
    setUser(null);
    setError(authError);
    setStatus('anonymous');
  }, []);

  /** Adopts a freshly minted session and confirms it against the server. */
  const adoptSession = React.useCallback(
    async (next: ISession) => {
      const adopted = ++sessionEpoch.current;
      sessionStore.set(next);
      setError(null);

      const account = await authApi.me();
      // A 401 answering a read the previous session made can land after `set` and drop this
      // one; adopting the answer then would be signed in with no session stored.
      if (sessionEpoch.current !== adopted) throw new Error('The session ended while it opened.');
      setUser(account);
      setStatus('authenticated');
    },
    []
  );

  // Any 401 — from an auth call or from `/api/v1` — means the server no longer
  // holds this session. One handler, registered once, drops it. A 401 answering a read made
  // for a session this browser has already dropped says nothing: the late `/auth/me` of a
  // session an email change signed out would otherwise leave "session expired"
  // on the sign-in screen beside the change's own note (SR-477). Nor does a 401 while a logout
  // leaves — its own `/auth/logout`, for a session revoked elsewhere: the session is ending
  // anyway, and "expired" would put `/login` between the press and the IdP's page.
  React.useEffect(() => {
    setUnauthorizedHandler(() => {
      if (sessionStore.get() && !leaving.current) dropSession(SESSION_EXPIRED);
    });
    return () => setUnauthorizedHandler(null);
  }, [dropSession]);

  // A 403 asks a question the body cannot answer: was this account's *permission* refused
  // here because it was taken away since `/auth/me` was last read (SR-228)? An administrator
  // changes a role from another browser, and without this a screen would keep offering what
  // survey-api now refuses, with nothing saying why, until the page happened to reload.
  //
  // Re-reading the account is the whole of it: if a permission went, the guards re-render
  // from the new `permissions`; if not, nothing observable happens. The refusal itself
  // still reaches the caller — the admin screens' own 403 copy is untouched, which is why this
  // costs them one extra GET per refusal and does not try to guess which kind of 403 it was.
  React.useEffect(() => {
    let rechecking = false;

    setForbiddenHandler(() => {
      // Coalesced: a page whose writes all fail at once must ask once, not once per request.
      if (rechecking) return;
      rechecking = true;
      const asked = sessionEpoch.current;
      void authApi
        .me()
        .then((account) => {
          // The request goes out with a token the server has not revoked yet, so it can
          // answer perfectly well about a session that has since ended — `sessionEpoch` is
          // what says whether this answer is still about the session that asked for it.
          if (sessionEpoch.current === asked) setUser(account);
        })
        .catch((meError) => {
          // A 401 has already gone through the handler above and dropped the session, so this
          // is a gateway blip or the API being down. Nothing is shown for it — the app is
          // left exactly as it was — but it is recorded rather than swallowed: this effect
          // exists so a respondent stops seeing "Could not save your answers" with no reason,
          // and an empty block here restores that symptom with no trace of why.
          console.warn('[Auth] Could not re-read the account after a 403:', meError);
        })
        .finally(() => {
          rechecking = false;
        });
    });

    return () => setForbiddenHandler(null);
  }, []);

  // First pass: a stored session is only believed once `/auth/me` confirms it —
  // the token may have been revoked (logout elsewhere, an email change, an unlinked identity,
  // purge) long before the `expiresAt` it was handed with.
  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      const stored = sessionStore.get();
      const validity = checkSession(stored);

      if (validity !== ValidationStatus.valid) {
        if (!cancelled) {
          dropSession(validity === ValidationStatus.expired ? SESSION_EXPIRED : null);
        }
        return;
      }

      // A session dropped while this read is out (a sign-out, a 401) is
      // gone: its late answer must not sign the browser back in (SR-477).
      const asked = sessionEpoch.current;
      try {
        const account = await authApi.me();
        if (cancelled || sessionEpoch.current !== asked) return;
        setUser(account);
        setStatus('authenticated');
      } catch {
        // A 401 already went through the unauthorized handler above; anything
        // else (the API being down) must not leave the app stuck on 'pending'.
        if (!cancelled && sessionEpoch.current === asked) dropSession();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dropSession]);

  // Heartbeat: local expiry only. There is nothing to renew, so this exists to
  // end the session at its stated time rather than at the next failed request.
  React.useEffect(() => {
    if (status !== 'authenticated') return undefined;

    const timerId = window.setInterval(() => {
      if (checkSession(sessionStore.get()) !== ValidationStatus.valid) {
        dropSession(SESSION_EXPIRED);
      }
    }, UPDATE_INTERVAL);

    return () => window.clearInterval(timerId);
  }, [dropSession, status]);

  const exchange = React.useCallback(
    async (accessToken: string, idToken: string) => {
      const session = await authApi.exchange(accessToken);
      try {
        await adoptSession({ ...session, idToken });
      } catch (meError) {
        // The session opened, and its first `/auth/me` failed: nothing is half-signed-in.
        dropSession();
        throw meError;
      }
    },
    [adoptSession, dropSession]
  );

  const signout = React.useCallback(() => {
    const idToken = sessionStore.get()?.idToken;
    if (!idToken) {
      // Nothing leaves the page, so nothing cancels the request: the session goes at once.
      void authApi.logout();
      dropSession();
      return;
    }
    leaving.current = true;
    setStatus('pending');
    void (async () => {
      // Awaited before the page leaves for the IdP, since a navigation would cancel the request
      // and the server-side session would outlive the logout — but only for so long: a stalled
      // API must not hold the splash. It never rejects.
      await authApi.logout(AbortSignal.timeout(LOGOUT_WAIT_MS));
      // Out of storage before the page leaves, so a late 401 or a reload finds no session. The
      // context is dropped only when Back restores this page from the back-forward cache, where
      // the splash would otherwise stand for good.
      sessionStore.remove();
      try {
        const endSession = await endSessionUrl(idToken);
        window.addEventListener(
          'pageshow',
          (event) => {
            if (event.persisted) dropSession();
          },
          { once: true }
        );
        window.location.assign(endSession);
      } catch (idpError) {
        // `endSessionUrl` spells the end session itself when discovery is out of reach, so only an
        // authority that is no URL lands here. Survey-api's session is gone either way; the
        // person is left on `/login`, signed out here and still signed in at the IdP.
        console.warn("[Auth] Could not open the IdP's end session:", idpError);
        dropSession();
      }
    })();
  }, [dropSession]);

  const refreshUser = React.useCallback(async () => {
    if (!sessionStore.get()) return;
    const asked = sessionEpoch.current;
    try {
      const account = await authApi.me();
      if (sessionEpoch.current === asked) setUser(account);
    } catch (meError) {
      console.warn('[Auth] Could not re-read the account:', meError);
    }
  }, []);

  const value = React.useMemo<IAuthContextValue>(
    () => ({
      status,
      isAuth: status === 'authenticated',
      user,
      error,
      exchange,
      signout,
      refreshUser
    }),
    [status, user, error, exchange, signout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): IAuthContextValue {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }

  return context;
}
